"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/lib/i18n/navigation";

/**
 * <CascadeBanner/> — Bandeau riche de feedback cascade de fallback recherche.
 *
 * Remplace le bandeau inline (string-only) du palier 5 dans /recherche/page.tsx.
 * Propose un rendu typé par stage + suggestions cliquables (did-you-mean,
 * nearby-hub) + CTA "êtes-vous médecin ?" sur zero-result.
 *
 * Stages :
 *   - "exact"                       → rend null (silent)
 *   - "advanced-filters-relaxed"    → bandeau accent (jaune sable)
 *   - "fallback-drop-name"          → bandeau info (cyan clair) + droppedTokens
 *   - "specialty-dropped-q"         → bandeau info (cyan clair) + droppedTokens
 *   - "fallback-expand-specialty"   → bandeau info + expandedFrom→expandedTo
 *   - "zero"                        → bandeau gris + suggestions + CTA Pro
 *
 * Accessibilité : role="status" + aria-live="polite" pour annoncer le
 * fallback aux lecteurs d'écran sans interrompre. Suggestions = <a href>
 * (Link i18n-aware) et pas <button> (vraie navigation, ouvrable en onglet).
 *
 * i18n : utilise les clés `search.page.broadened*` déjà en place (paliers
 * 1-5) + 3 nouvelles clés (`didYouMean`, `nearbyHub`, `zeroResultCta`).
 */

export type CascadeBannerStage =
  | "exact"
  | "advanced-filters-relaxed"
  | "fallback-drop-name"
  | "specialty-dropped-q"
  | "fallback-expand-specialty"
  | "zero";

export type CascadeBannerSuggestion = {
  kind: "did-you-mean" | "nearby-hub";
  label: string;
  href: string;
  cityName?: string;
  suggestion?: string;
  original?: string;
};

export type CascadeBannerProps = {
  stage: CascadeBannerStage;
  /** Tokens supprimés (palier fallback-drop-name / specialty-dropped-q). */
  droppedTokens?: string[];
  /** Spécialité étroite d'origine (palier fallback-expand-specialty). */
  expandedFrom?: string;
  /** Spécialité parente cible (palier fallback-expand-specialty). */
  expandedTo?: string;
  /** Spécialité résolue affichée en contexte (ex: "voici les {specialty}…"). */
  specialty?: string;
  /** Ville résolue affichée en contexte. */
  city?: string;
  /** Suggestions cliquables — chips affichées dans le bandeau zero. */
  suggestions?: CascadeBannerSuggestion[];
};

function IconAlertCircle({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
    </svg>
  );
}

function IconSparkles({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path
        d="M12 3l1.8 4.6L18.4 9.4l-4.6 1.8L12 16l-1.8-4.8L5.6 9.4l4.6-1.8L12 3zM19 14l.9 2.3L22 17l-2.1.8L19 20l-.9-2.2L16 17l2.1-.8L19 14zM5 14l.9 2.3L8 17l-2.1.8L5 20l-.9-2.2L2 17l2.1-.8L5 14z"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconMapPin({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d="M12 22s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function IconArrow({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path
        d="M5 12h14M13 6l6 6-6 6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CascadeBanner({
  stage,
  droppedTokens,
  expandedFrom,
  expandedTo,
  specialty,
  city,
  suggestions,
}: CascadeBannerProps) {
  const t = useTranslations("search");

  if (stage === "exact") return null;

  // ── Palier 5 : filtres avancés relâchés — variant accent (sable/jaune)
  if (stage === "advanced-filters-relaxed") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="mb-3 flex items-start gap-2 rounded-xl border-l-4 border-[var(--sahha-accent-200)] border-l-[var(--sahha-accent-400)] bg-[var(--sahha-accent-50)] px-3.5 py-2.5 text-[12.5px] leading-snug text-[var(--sahha-accent-800)]"
      >
        <IconAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{t("page.broadenedAdvancedFilters")}</span>
      </div>
    );
  }

  // ── Paliers fallback-drop-name & specialty-dropped-q — variant info (cyan)
  if (stage === "fallback-drop-name" || stage === "specialty-dropped-q") {
    const droppedName = droppedTokens?.join(" ") ?? "";
    return (
      <div
        role="status"
        aria-live="polite"
        className="mb-3 flex items-start gap-2 rounded-xl border-l-4 border-[color:var(--sahha-info-bg,#e8f1f9)] border-l-[color:var(--sahha-info,#3b82f6)] bg-[color:var(--sahha-info-bg,#e8f1f9)] px-3.5 py-2.5 text-[12.5px] leading-snug text-[var(--sahha-text-700)]"
      >
        <IconSparkles className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--sahha-info,#3b82f6)]" />
        <span>
          {droppedName
            ? t("page.broadenedDropName", { name: droppedName })
            : specialty && city
              ? t("page.broadenedSpecialtyCity", { city })
              : t("page.broadenedAdvancedFilters")}
        </span>
      </div>
    );
  }

  // ── Palier fallback-expand-specialty — variant info (cyan)
  if (stage === "fallback-expand-specialty") {
    const from = expandedFrom ?? specialty ?? "";
    const to = expandedTo ?? "";
    return (
      <div
        role="status"
        aria-live="polite"
        className="mb-3 flex items-start gap-2 rounded-xl border-l-4 border-[color:var(--sahha-info-bg,#e8f1f9)] border-l-[color:var(--sahha-info,#3b82f6)] bg-[color:var(--sahha-info-bg,#e8f1f9)] px-3.5 py-2.5 text-[12.5px] leading-snug text-[var(--sahha-text-700)]"
      >
        <IconSparkles className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--sahha-info,#3b82f6)]" />
        <span>{t("page.broadenedExpandSpecialty", { specialty: from, parent: to })}</span>
      </div>
    );
  }

  // ── Stage "zero" : bandeau gris + suggestions cliquables + CTA Pro
  // Limite à 3 suggestions max (UX scan-friendly).
  const visibleSuggestions = (suggestions ?? []).slice(0, 3);

  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-3 rounded-xl border-l-4 border-[var(--sahha-border-subtle)] border-l-[var(--sahha-text-400,#94a3b8)] bg-[var(--sahha-surface-1)] px-3.5 py-3"
    >
      <div className="flex items-start gap-2">
        <IconAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--sahha-text-600)]" />
        <p className="text-[12.5px] font-semibold leading-snug text-[var(--sahha-text-900)]">
          {t("empty.title")}
        </p>
      </div>

      {visibleSuggestions.length > 0 && (
        <ul className="mt-2.5 flex flex-wrap gap-2">
          {visibleSuggestions.map((s, i) => {
            const Icon = s.kind === "did-you-mean" ? IconSparkles : IconMapPin;
            const label =
              s.kind === "did-you-mean" && s.suggestion
                ? t("page.didYouMean", { suggestion: s.suggestion })
                : s.kind === "nearby-hub" && s.cityName
                  ? t("page.nearbyHub", { cityName: s.cityName })
                  : s.label;
            return (
              <li key={`${s.kind}-${i}-${s.href}`}>
                <Link
                  href={s.href as never}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[var(--sahha-border-default)] bg-white px-3 py-1.5 text-[12.5px] font-semibold text-[var(--sahha-primary-700)] transition hover:border-[var(--sahha-primary-400)] hover:bg-[var(--sahha-primary-50)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate max-w-[220px]">{label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--sahha-border-subtle)] pt-2.5">
        <Link
          href="/recherche"
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--sahha-primary-700)] px-3.5 py-1.5 text-[12.5px] font-semibold text-white transition hover:bg-[var(--sahha-primary-800,#0e6e87)] focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)]"
        >
          {t("empty.cta")}
          <IconArrow className="h-3.5 w-3.5 shrink-0" />
        </Link>
        <Link
          href="/espace-pro"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--sahha-text-600)] underline-offset-2 hover:text-[var(--sahha-primary-700)] hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--sahha-primary-200)] rounded"
        >
          {t("page.zeroResultCta")}
        </Link>
      </div>
    </div>
  );
}

export default CascadeBanner;
